/**
 * Favorite navigation entries, saved per user in the profile's ui_preferences.favorites (list of route paths).
 * Updates are optimistic so the star flips immediately; the server merges ui_preferences shallowly.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useProfile, type Profile } from "../accounts_user_profiles/useData";
import { api } from "../api/client";

export const FAVORITES_KEY = "favorites";

function readFavorites(profile: Profile | undefined): string[] {
  const raw = profile?.ui_preferences?.[FAVORITES_KEY];
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : [];
}

export function useFavorites() {
  const qc = useQueryClient();
  const profile = useProfile();
  const favorites = readFavorites(profile.data);
  const save = useMutation({
    mutationFn: async (next: string[]) => {
      const { data, error } = await api.PATCH("/api/v1/accounts/me/profile/", { body: { ui_preferences: { [FAVORITES_KEY]: next } } as never });
      if (error) throw error;
      return data as unknown as Profile;
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ["profile"] });
      const previous = qc.getQueryData<Profile>(["profile"]);
      if (previous) qc.setQueryData<Profile>(["profile"], { ...previous, ui_preferences: { ...previous.ui_preferences, [FAVORITES_KEY]: next } });
      return { previous };
    },
    onError: (_err, _next, ctx) => { if (ctx?.previous) qc.setQueryData(["profile"], ctx.previous); },
    onSuccess: (data) => { qc.setQueryData(["profile"], data); },
  });
  const isFavorite = useCallback((to: string) => favorites.includes(to), [favorites]);
  const toggle = useCallback((to: string) => {
    const next = favorites.includes(to) ? favorites.filter((f) => f !== to) : [...favorites, to];
    save.mutate(next);
  }, [favorites, save]);
  return { favorites, isFavorite, toggle, loaded: profile.isSuccess };
}
